/**
 * addit Theme — measurement events (5 Oct 2026, homepage rebuild "Measurement before the switch").
 *
 * Loaded once from layout/theme.liquid on the index and product templates and on the landing page template
 * page.proteinreich-kochen (never in the theme editor), with data-template = "index", "index.staging",
 * "index.original", "product", "product.starter-set" or "page.proteinreich-kochen". page_type is "home", "pdp",
 * "lp" (the landing page, Oct 2026: it reuses the homepage section keys hp_hero, hp_set, hp_steps, hp_faq, hp_closer,
 * so TYPES and CTA_WHERE apply unchanged; its Set box carries the id "starter-set", so the hero and closer buttons
 * count as hero_offer / closer_offer and the sticky bar as hp_set_sticky) or "other". It publishes
 * three custom events through Shopify.analytics.publish. The custom pixel "addit PDP events"
 * (analytics/pdp-events-pixel.js) forwards them to GA4, and only with the visitor's analytics consent.
 *
 *   addit_section_view  { page_type, template, section_key, section_type, position, section_count, product_handle }
 *     Once per section per page view: the first time >= 50 % of the section is on screen, or >= 400 px of it
 *     (on a viewport lower than 500 px: 80 % of the viewport's height). The template's own header section is left
 *     out, so position 1 is the hero on every homepage. Sections that are empty at load (e.g. pdp_set on the Set
 *     page) are left out too.
 *   addit_atc           { atc_source, section_key, page_type, template, product_handle, line_count, via_cta }
 *     Once per SUCCESSFUL POST /cart/add.js (HTTP 200), so one per Starter Set add (Shopify's own
 *     product_added_to_cart fires once per line, i.e. twice for the Set). atc_source names the control the visitor
 *     pressed: launch_offer, launch_sticky, hp_set, product_main, product_sticky, cart_cross_sell; another section's
 *     button gives its key (hyphens become underscores), a sticky bar "<key>_sticky"; no recent press = "other".
 *     The parameter is not called "source": GA4 reads event parameters named source / medium / campaign as traffic
 *     source. via_cta = the last same-page anchor CTA to the product form pressed within 10 minutes before the add
 *     (header_cta, hero_offer, closer_offer: the live buttons that scroll to #launch-offer), else ""; it is used up
 *     by that add. Other anchors ("So funktioniert's" → #launch-how, footnotes, #pdp-detail-…) never set it.
 *   addit_cta_click     { page_type, template, section_key, cta, kind, target }
 *     A link or button-link pressed inside <main> or the header. cta: header_cta | header_nav | header_logo, else
 *     <where>_<what>, plus "_link" for a plain text link. where = hero | closer (the hero and closer sections of
 *     every homepage) or the section key with underscores (hp_set, launch_offer …); what = offer (same-page anchor
 *     to the section with the product form) | the anchor's name without "launch-" (how, ein_loeffel …) | pdp (a
 *     /products/ page) | nothing (any other link). So each control has its own name: hero_offer / hero_how,
 *     closer_offer / closer_how, hero_pdp, closer_pdp, hp_set_pdp_link.
 *     kind: button | link. target: path + #hash, never the query string; another site = "external:<host>".
 *     Clicks that leave the page can be lost (the pixel's sandbox closes), so read these counts as a lower bound.
 *
 * How an add is seen: every theme add (assets/product.js, assets/cart-drawer.js) is a fetch POST to /cart/add.js.
 * window.fetch is wrapped transparently: the caller gets the very same promise back, and the response is only read
 * through clone() in a side branch. The source is the last real (isTrusted) click on an add control, so the sticky
 * bars' synthetic mainBtn.click() is ignored. A blocked add (Set already in the cart) makes no /cart/add.js call and
 * sends nothing.
 *
 * Rules: no PII (no customer, cart token, e-mail, price); no storage; never blocks or delays a click, a navigation
 * or an add; every failure is silent. Publishing does not depend on consent; the pixel decides whether to forward.
 * Section types come from the table below (the template JSON keys of 5 Oct 2026); for a key the editor generated
 * later ("<type>_Ab12Cd") the type is read from the key, otherwise it is "". The warehouse query
 * (warehouse/analysis/homepage_funnel.sql) maps keys to cross-template roles.
 */
(function () {
  'use strict';

  try {
    if (window.Shopify && window.Shopify.designMode) return;

    var script = document.currentScript;
    var template = (script && script.getAttribute('data-template')) || '';
    var LP_TEMPLATES = { 'page.proteinreich-kochen': 1 };
    var pageType = template.indexOf('index') === 0 ? 'home' : template.indexOf('product') === 0 ? 'pdp' : LP_TEMPLATES[template] ? 'lp' : 'other';
    var productHandle = '';
    if (pageType === 'pdp') {
      var hm = /\/products\/([^\/?#]+)/.exec(window.location.pathname);
      if (hm) {
        try {
          productHandle = decodeURIComponent(hm[1]);
        } catch (e) {
          productHandle = hm[1];
        }
      }
    }

    var INTENT_MS = 15000; // press → successful add (the Set path first reads /cart.js)
    var CTA_MS = 600000; // same-page CTA → add, for via_cta

    var TYPES = {
      'launch-header': 'launch-header',
      'launch-hero': 'launch-hero',
      'launch_hero_flex_HpAJnf': 'launch-hero-flex',
      'launch-product-intro': 'launch-product-intro',
      'launch-how': 'landing-page-how-it-works',
      'launch_recipe_cCGcbc': 'launch-recipe',
      'launch-offer': 'launch-offer',
      'launch_usage_showcase_jz4Xnk': 'launch-usage-showcase',
      'launch-problem': 'launch-problem',
      'launch-proof': 'launch-proof',
      'launch-science': 'launch-science',
      'launch-founders': 'launch-founders',
      'launch-faq': 'faq-accordion',
      'mission': 'mission',
      'reels_grid': 'reels-grid',
      'journal_teaser': 'journal-teaser',
      'launch-cta': 'landing-page-cta',
      'hp_hero': 'hp-hero',
      'hp_set': 'hp-set-moment',
      'hp_tiles': 'hp-media-tiles',
      'hp_unbox': 'hp-unboxing',
      'hp_steps': 'hp-steps',
      'hp_pasta': 'hp-recipe',
      'hp_loeffel': 'hp-usage',
      'hp_bedarf': 'hp-proof',
      'hp_idee': 'hp-founders',
      'hp_faq': 'hp-faq',
      'hp_closer': 'hp-cta',
      'hp_basis': 'hp-basis',
      'lp_dishes': 'lp-dishes',
      'lp_physics': 'lp-physics',
      'lp_limits': 'lp-limits',
      'product-main': 'product-main',
      'product-comparison': 'product-comparison',
      'cta-banner': 'cta-banner',
      'pdp_set': 'pdp-set',
      'pdp_unbox': 'hp-unboxing',
      'pdp-definition': 'pdp-definition',
      'pdp_steps': 'hp-steps',
      'reels': 'reels-grid',
      'pdp-quotes': 'pdp-quotes',
      'pdp_table': 'pdp-category-comparison',
      'amino-chart': 'science-amino-chart',
      'faq': 'faq-accordion',
      'cart-drawer': 'cart-drawer'
    };
    var STICKY = { 'launch-offer': 'launch_sticky', 'product-main': 'product_sticky' };

    var publish = function (name, data) {
      try {
        var analytics = window.Shopify && window.Shopify.analytics;
        if (analytics && typeof analytics.publish === 'function') analytics.publish(name, data);
      } catch (e) {}
    };
    var noop = function () {};
    var now = function () {
      return new Date().getTime();
    };
    var cut = function (value) {
      return String(value == null ? '' : value).slice(0, 100);
    };
    var norm = function (key) {
      return String(key || '').replace(/-/g, '_');
    };
    var sectionOf = function (el) {
      return el && el.closest ? el.closest('.shopify-section') : null;
    };
    // "shopify-section-template--<digits>__<key>" → "<key>" (the digits change whenever the template is saved)
    var keyOf = function (section) {
      var id = ((section && section.id) || '').replace(/^shopify-section-/, '');
      var i = id.lastIndexOf('__');
      return i > -1 ? id.slice(i + 2) : id;
    };
    var typeOf = function (key) {
      if (Object.prototype.hasOwnProperty.call(TYPES, key)) return TYPES[key];
      var m = /^(.+)_([A-Za-z0-9]{6})$/.exec(key);
      if (m && /[A-Z0-9]/.test(m[2])) return m[1].replace(/_/g, '-');
      return '';
    };
    var isHeader = function (key) {
      return /(^|[-_])header([-_]|$)/.test(key);
    };

    /* ------------------------------------------------------------------------
       Section views
       ------------------------------------------------------------------------ */
    var watchSections = function () {
      try {
        if (!('IntersectionObserver' in window)) return;
        var all = document.querySelectorAll('main .shopify-section');
        var list = [];
        for (var i = 0; i < all.length; i++) {
          var key = keyOf(all[i]);
          if (!key || isHeader(key)) continue;
          if (!all[i].getClientRects().length || !all[i].offsetHeight) continue;
          list.push(all[i]);
        }
        if (!list.length) return;
        var steps = [];
        for (var s = 0; s <= 40; s++) steps.push(s / 40);
        var observer = new IntersectionObserver(function (entries) {
          try {
            var minPx = Math.min(400, 0.8 * (window.innerHeight || 800));
            for (var j = 0; j < entries.length; j++) {
              var entry = entries[j];
              if (!entry.isIntersecting) continue;
              var height = entry.boundingClientRect.height;
              var visible = entry.intersectionRect.height;
              if (!height || (visible / height < 0.5 && visible < minPx)) continue;
              observer.unobserve(entry.target);
              var sectionKey = keyOf(entry.target);
              publish('addit_section_view', {
                page_type: pageType,
                template: template,
                section_key: cut(sectionKey),
                section_type: typeOf(sectionKey),
                position: list.indexOf(entry.target) + 1,
                section_count: list.length,
                product_handle: productHandle
              });
            }
          } catch (e) {}
        }, { threshold: steps });
        for (var k = 0; k < list.length; k++) observer.observe(list[k]);
      } catch (e) {}
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', watchSections);
    } else {
      watchSections();
    }

    /* ------------------------------------------------------------------------
       Presses: add controls (intent for the next add) and CTA links
       ------------------------------------------------------------------------ */
    var intent = null; // { source, key, t }
    var lastCta = null; // { cta, t }

    // cta names: hero and closer buttons are named by role, so the live and the new homepage compare.
    var CTA_WHERE = {
      'launch_hero_flex_HpAJnf': 'hero',
      'launch-hero': 'hero',
      'hp_hero': 'hero',
      'launch-cta': 'closer',
      'hp_closer': 'closer'
    };
    // The element a same-page #hash scrolls to (null if there is none).
    var hashTarget = function (url) {
      try {
        var id = decodeURIComponent(url.hash.slice(1));
        return id ? document.getElementById(id) : null;
      } catch (e) {
        return null;
      }
    };
    // True when the target is or holds the product add-to-cart form (#launch-offer → the launch-offer section). A
    // link to something inside the form (#pdp-detail-… opens an info row in the PDP buy box) does not count.
    var holdsProductForm = function (el) {
      if (!el || !el.querySelector) return false;
      return (el.matches && el.matches('[data-product-info]')) || !!el.querySelector('[data-product-info]');
    };
    // "#launch-how" → "how", "#ein-loeffel" → "ein_loeffel"
    var anchorName = function (url) {
      var h = url.hash.slice(1);
      try {
        h = decodeURIComponent(h);
      } catch (e) {}
      return norm(h.replace(/^launch-/, '')).replace(/[^A-Za-z0-9_]/g, '').slice(0, 40) || 'anchor';
    };

    var ctaClick = function (a) {
      var href = a.getAttribute('href') || '';
      if (!href || /^(javascript|mailto|tel):/i.test(href) || a.hasAttribute('data-cart-link')) return;
      var url;
      try {
        url = new URL(href, window.location.href);
      } catch (e) {
        return;
      }
      var sameOrigin = url.origin === window.location.origin;
      var samePageAnchor = sameOrigin && url.pathname === window.location.pathname && !!url.hash;
      var toOffer = samePageAnchor && holdsProductForm(hashTarget(url));
      var inHeader = !!a.closest('.launch-header');
      var section = sectionOf(a);
      var key = section ? keyOf(section) : inHeader ? 'header' : '';
      var isButton = a.classList.contains('btn') || a.classList.contains('hp-btn');
      var cta;
      if (inHeader) {
        cta = a.classList.contains('btn') ? 'header_cta' : a.closest('nav') ? 'header_nav' : 'header_logo';
      } else {
        // <where>_<what>: hero_offer / hero_how, closer_offer / closer_how, closer_pdp, hp_set_pdp_link …
        var what = samePageAnchor ? (toOffer ? 'offer' : anchorName(url)) : sameOrigin && /^\/products\//.test(url.pathname) ? 'pdp' : '';
        cta = (CTA_WHERE[key] || norm(key) || 'page') + (what ? '_' + what : '') + (isButton ? '' : '_link');
      }
      publish('addit_cta_click', {
        page_type: pageType,
        template: template,
        section_key: cut(key),
        cta: cut(cta),
        kind: isButton ? 'button' : 'link',
        target: cut(sameOrigin ? url.pathname + url.hash : 'external:' + url.hostname)
      });
      // Only a same-page anchor to the section with the product form (the live header / hero / closer button →
      // #launch-offer) is remembered for the add that may follow. "So funktioniert's" (#launch-how), footnotes and
      // links into the PDP buy box's info rows are not.
      if (toOffer) {
        lastCta = { cta: cta, t: now() };
      }
    };

    document.addEventListener('click', function (e) {
      try {
        if (!e.isTrusted || !e.target || !e.target.closest) return;
        var control = e.target.closest('[data-cross-sell-add], [data-sticky-atc], [data-add-to-cart]');
        if (control) {
          var key = keyOf(sectionOf(control));
          var source;
          if (control.hasAttribute('data-cross-sell-add')) {
            source = 'cart_cross_sell';
          } else if (control.hasAttribute('data-sticky-atc')) {
            source = STICKY[key] || (key ? norm(key) + '_sticky' : 'sticky');
          } else {
            source = norm(key) || 'buybox';
          }
          intent = { source: source, key: key, t: now() };
          return;
        }
        var a = e.target.closest('a[href]');
        if (a && (a.closest('main') || a.closest('.launch-header'))) ctaClick(a);
      } catch (err) {}
    }, true);

    // A product form submitted without a recent press (e.g. Enter in a field) still gets its section as source.
    document.addEventListener('submit', function (e) {
      try {
        var form = e.target;
        if (!form || !form.closest || !form.closest('[data-product-info]')) return;
        if (intent && now() - intent.t < 2000) return;
        var key = keyOf(sectionOf(form));
        intent = { source: norm(key) || 'buybox', key: key, t: now() };
      } catch (err) {}
    }, true);

    /* ------------------------------------------------------------------------
       Successful adds: a transparent observer on window.fetch
       ------------------------------------------------------------------------ */
    var isCartAdd = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      var method = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
      return String(method).toUpperCase() === 'POST' && /\/cart\/add(\.js)?(\?|#|$)/.test(url);
    };

    var reportAdd = function (pressed, data) {
      try {
        var items = data && data.items ? data.items : data ? [data] : [];
        var first = items[0] || {};
        var bundleId = first.properties && first.properties._bundle_id;
        var via = lastCta && now() - lastCta.t <= CTA_MS ? lastCta.cta : '';
        lastCta = null; // a CTA leads to one add, not to the cross-sell after it
        publish('addit_atc', {
          atc_source: pressed.source,
          section_key: cut(pressed.key),
          page_type: pageType,
          template: template,
          product_handle: cut(bundleId || first.handle || ''),
          line_count: items.length,
          via_cta: via
        });
      } catch (e) {}
    };

    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var promise = nativeFetch.apply(this, arguments);
        try {
          if (isCartAdd(input, init)) {
            var pressed = intent && now() - intent.t <= INTENT_MS ? intent : { source: 'other', key: '' };
            intent = null;
            promise.then(function (res) {
              try {
                if (!res || !res.ok || typeof res.clone !== 'function') return;
                res.clone().json().then(function (data) {
                  reportAdd(pressed, data);
                }, noop);
              } catch (e) {}
            }, noop);
          }
        } catch (e) {}
        return promise;
      };
    }
  } catch (e) {}
})();
